import React from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from 'src/components/ui/card';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { cn } from 'src/lib/utils';
import { AspectRatio } from 'src/components/ui/aspect-ratio';
import { If } from 'src/components/If';
import { FileIcon } from 'src/components/FileIcon';
import { formatSize } from 'src/helpers/file';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { Button } from 'src/components/ui/button';
import { Href } from 'src/components/ui/href';
import { Clock, Download, FolderArchive } from 'lucide-react';
import { mediaClient } from 'src/lib/services/api/media-api';
import { authQueries } from 'src/api/queries';
import { useToast } from 'src/components/ui/use-toast';

export const ComposedMediaCard: React.FC<
  React.ComponentProps<typeof Card> & {
    fileName: string;
    fileSize: number;
    mimeType: string;
    download?: string;
    thumbnail?: string;
    actions?: React.ReactNode;
    mediaId?: string;
    requestId?: string;
    archivedAt?: string;
    restorationStatus?: string;
    restorationDownloadUrl?: string;
    restorationInitializedAt?: string;
  }
> = ({
  fileName,
  fileSize,
  mimeType,
  download,
  className,
  thumbnail,
  actions,
  mediaId,
  requestId,
  archivedAt,
  restorationStatus,
  restorationDownloadUrl,
  restorationInitializedAt,
  ...props
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const client = useQueryClient();

  const restoreMutation = useMutation({
    mutationFn: (id: string) => mediaClient.restore(id),
    onSuccess: () => {
      if (requestId) {
        client.invalidateQueries({
          queryKey: [authQueries.request, requestId],
        });
      }
    },
    onError: () => {
      toast({
        title: t('Failed to restore file. Please try again.')!,
        variant: 'destructive',
      });
    },
  });

  const isArchived = !!archivedAt;
  const isRestoring =
    isArchived && !!restorationInitializedAt && restorationStatus !== 'complete';
  const isRestorationReady =
    isArchived && restorationStatus === 'complete' && !!restorationDownloadUrl;
  const needsUnarchive = isArchived && !isRestoring && !isRestorationReady;

  return (
    <Card className={cn(className)} {...props}>
      <CardContent className={'tw-w-full tw-items-stretch tw-p-0'}>
        <AspectRatio ratio={16 / 9}>
          <div className={'tw-relative tw-h-full tw-w-full'}>
            <div className={'tw-absolute tw-inset-0'}>
              <If
                when={!!thumbnail && !isArchived}
                else={
                  <div
                    className={cn(
                      'tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-rounded-t-md',
                      isArchived ? 'tw-bg-neutral-100' : 'tw-bg-indigo-200',
                    )}
                  >
                    {isArchived ? (
                      <FolderArchive size={36} className={'tw-text-neutral-400'} />
                    ) : (
                      <FileIcon mimeType={mimeType} size={36} className={'tw-text-dark-800'} />
                    )}
                  </div>
                }
              >
                <img
                  className={'tw-h-full tw-w-full tw-rounded-t-lg tw-object-cover'}
                  src={thumbnail}
                  alt={fileName}
                  title={fileName}
                />
              </If>
            </div>

            <div className={'tw-absolute tw-inset-0 tw-z-10 tw-flex tw-items-stretch tw-p-2'}>
              {/*<Badge className={'tw-self-start'} variant={'default'}>*/}
              {/*  Overlay*/}
              {/*</Badge>*/}
            </div>
          </div>
        </AspectRatio>
      </CardContent>
      <CardHeader className={'tw-gap-2 tw-px-3 tw-pb-0 tw-pt-3'}>
        <CardTitle
          className={
            'tw-line-clamp-2 tw-h-10 tw-w-full tw-break-all tw-text-base tw-font-bold tw-leading-normal tw-text-dark'
          }
        >
          {fileName}
        </CardTitle>
        <CardDescription className={'tw-line-clamp-1 tw-text-base'}>
          {mimeType} | {formatSize(fileSize)}
        </CardDescription>
      </CardHeader>
      <CardFooter className={'tw-justify-end tw-px-3 tw-pb-3 tw-pt-0'}>
        {actions}

        {needsUnarchive && mediaId && (
          <TooltipProvider>
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button
                  size={'sm'}
                  variant={'outline'}
                  className={'tw-gap-1'}
                  onClick={() => restoreMutation.mutate(mediaId)}
                  disabled={restoreMutation.isLoading}
                >
                  <FolderArchive size={16} />
                  {restoreMutation.isLoading ? t('Restoring...') : t('Unarchive')}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {t('This file is archived. Click to unarchive (may take up to 24 hours).')}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        {isRestoring && (
          <TooltipProvider>
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <span
                  className={
                    'tw-flex tw-items-center tw-gap-1 tw-text-sm tw-text-neutral-400'
                  }
                >
                  <Clock size={16} />
                  {t('Restoring...')}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {t('File is being unarchived (may take up to 24 hours)')}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        {isRestorationReady && (
          <TooltipProvider>
            <Tooltip delayDuration={0}>
              <TooltipTrigger>
                <Button size={'icon'} variant={'ghost'} asChild>
                  <Href
                    variant={'unset'}
                    href={restorationDownloadUrl}
                    target={'_blank'}
                    rel={'noreferrer'}
                  >
                    <Download size={20} />
                  </Href>
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('Download')}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        {!isArchived && !!download && (
          <TooltipProvider>
            <Tooltip delayDuration={0}>
              <TooltipTrigger>
                <Button size={'icon'} variant={'ghost'} asChild>
                  <Href variant={'unset'} href={download} target={'_blank'} rel={'noreferrer'}>
                    <Download size={20} />
                  </Href>
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('Download')}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </CardFooter>
    </Card>
  );
};
